import React, { Component } from 'react';
import { observer, inject } from 'mobx-react';
import {
  List,
  Icon,
  Popup,
  PopupHeader,
  PopupContent,
} from 'semantic-ui-react';
import { withTranslation } from 'react-i18next';
import Chart from 'chart.js/auto';
import HeaderLink from '../SubHeader/HeaderLink';
import api from '../../api';
import { convertPropertiesToCamelCase } from '../../utils/shared';
import './OverviewPanel.css';

class OverviewPanel extends Component {
  constructor(props) {
    super(props);
    this.chart = null;
    this.state = {
      isLoading: false,
      overviewSummary: {},
      componentCount: {
        total: 0,
        has_risk: 0,
      }
    };
  }

  componentDidMount() {
    this.onUpdateLoading();
  }

  componentDidUpdate(prevProps, prevState) {
    const { isLoading, componentCount } = this.state;

    if (prevState.componentCount.total !== componentCount.total) {
      this.drawChart();
    }
    if (prevState.isLoading !== isLoading && isLoading) {
      this.getOverviewSummary();
      this.setState({
        isLoading: false,
      });
    }
  }

  componentWillUnmount() {
    if (this.chart) {
      this.chart.destroy();
    }
  }

  onUpdateLoading() {
    this.setState({
      isLoading: true,
    });
  }

  getOverviewSummary() {
    const { state } = this.props;
    api
      .getOverviewSummary()
      .then((res) => {
        const data = convertPropertiesToCamelCase(res.response);
        this.setState({
          overviewSummary: data,
          componentCount: data.componentCount,
        });
        state.company.setOverviewSummary(data);
      })
      .catch((err) => {
        console.log(err);
      });
  }

  abbreviateNumber(value) {
    let newValue = value;
    if (value >= 10000) {
      const suffixes = ['', 'K', 'M', 'B', 'T'];
      const suffixNum = Math.floor(` ${value}`.length / 4);
      let shortValue = '';
      let i = 4;
      for (i; i >= 1; i--) {
        shortValue = parseFloat(
          (suffixNum !== 0 ? value / (1000 ** suffixNum) : value).toPrecision(i) // prettier-ignore
        );
        const dotLessShortValue = `${shortValue} `.replace(/[^a-zA-Z 0-9]+/g, '');
        if (dotLessShortValue.length <= 4) {
          break;
        }
      }
      newValue = shortValue + suffixes[suffixNum];
    }
    return newValue;
  }

  drawChart() {
    const { componentCount } = this.state;
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }

    const config = {
      type: 'pie',
      data: {
        datasets: [
          {
            label: ['risk', 'safety'],
            data: [
              componentCount.has_risk,
              componentCount.total - componentCount.has_risk
            ],
            backgroundColor: ['#FF7D00', '#8A6FEB'],
            borderWidth: 0,
            hoverOffset: 4
          },
        ],
      },
      options: {
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            enabled: false,
          },
        },
      },
    };

    const ctx = this.pieChart.getContext('2d');
    this.chart = new Chart(ctx, config);
  }

  render() {
    const { t } = this.props;
    const { overviewSummary } = this.state;
    const {
      companyGrade,
      companyName,
      hasExploit,
      componentCount,
      securityCount,
      licenseCount,
      litigatorCount,
      policyCount,
    } = overviewSummary;

    return (
      <div>
        <div className="result_header">
          <div className="result_header_inner">
            <div className="result_header_box">
              <h2 className="name">
                {companyName}
                <div className="icon_wrap">
                  <a className="icon_box">
                    <svg
                      width="18px"
                      height="16px"
                      viewBox="0 0 18 16"
                      version="1.1"
                      xmlns="http://www.w3.org/2000/svg"
                      xlinkHref="http://www.w3.org/1999/xlink"
                    >
                      <g
                        id="02.-Result"
                        stroke="none"
                        strokeWidth="1"
                        fill="none"
                        fillRule="evenodd"
                        opacity="0.5"
                      >
                        <g
                          id="result-/-historyBtn"
                          transform="translate(0.000000, -2.000000)"
                          fill="#FFFFFF"
                          fillRule="nonzero"
                        >
                          <g id="Group" transform="translate(0.000000, 2.000000)">
                            <path
                              d="M4.03859443,7.31570313 L5.27722486,7.31570313 C5.44291029,7.31570313 5.57722486,7.45001771 5.57722486,7.61570313 C5.57722486,7.67944506 5.55692215,7.74153023 5.51926062,7.79295632 L3.42046327,10.658823 C3.32256904,10.7924956 3.13484698,10.8214998 3.00117433,10.7236055 C2.97639075,10.7054554 2.95454184,10.6836065 2.93639176,10.658823 L0.837594413,7.79295632 C0.739700179,7.65928367 0.768704322,7.47156161 0.902376978,7.37366738 C0.953803066,7.33600584 1.01588824,7.31570313 1.07963017,7.31570313 L2.02885649,7.31570313 C2.37600075,3.21774696 5.81222784,0 10,0 C14.418278,0 18,3.581722 18,8 C18,12.418278 14.418278,16 10,16 C8.29959642,16 6.67679195,15.4671014 5.32587784,14.4931426 C4.87788442,14.170156 4.77654637,13.5451532 5.09953296,13.0971598 C5.42251956,12.6491664 6.04752235,12.5478283 6.49551577,12.8708149 C7.50862794,13.6012311 8.72297522,14 10,14 C13.3137085,14 16,11.3137085 16,8 C16,4.6862915 13.3137085,2 10,2 C6.91768345,2 4.37821978,4.32422952 4.03859443,7.31570313 Z M13,8.25 C13.4142136,8.25 13.75,8.58578644 13.75,9 C13.75,9.41421356 13.4142136,9.75 13,9.75 L10,9.75 C9.58578644,9.75 9.25,9.41421356 9.25,9 L9.25,5 C9.25,4.58578644 9.58578644,4.25 10,4.25 C10.4142136,4.25 10.75,4.58578644 10.75,5 L10.75,8.25 L13,8.25 Z"
                              id="Combined-Shape"
                            />
                          </g>
                        </g>
                      </g>
                    </svg>
                  </a>
                  <a className="icon_box">
                    <svg
                      width="14px"
                      height="16px"
                      viewBox="0 0 14 16"
                      version="1.1"
                      xmlns="http://www.w3.org/2000/svg"
                      xlinkHref="http://www.w3.org/1999/xlink"
                    >
                      <g
                        id="02.-Result"
                        stroke="none"
                        strokeWidth="1"
                        fill="none"
                        fillRule="evenodd"
                        opacity="0.5"
                      >
                        <g
                          id="result-/-bookmarkBtn-(normal)"
                          transform="translate(-3.000000, -2.000000)"
                          fill="#FFFFFF"
                          fillRule="nonzero"
                        >
                          <path
                            d="M10,14.8719376 L4.47816827,17.8782682 C3.8115753,18.2411911 2.9995355,17.7583732 3.00000019,16.9993878 L3.00857162,2.99938776 C3.0089096,2.44734219 3.45652576,2 4.00857143,2 L16,2 C16.5522847,2 17,2.44771525 17,3 L17,17 C17,17.7587154 16.1881874,18.2410619 15.5218317,17.8782682 L10,14.8719376 Z M15,4 L5.71428571,4 L5.00795937,4 L5.00103097,15.3163899 L9.52183173,12.8550651 C9.81995032,12.6927561 10.1800497,12.6927561 10.4781683,12.8550651 L15,15.3169513 L15,4 Z"
                            id="Shape"
                          />
                        </g>
                      </g>
                    </svg>
                  </a>
                  <a className="icon_box">
                    <svg
                      width="16px"
                      height="4px"
                      viewBox="0 0 16 4"
                      version="1.1"
                      xmlns="http://www.w3.org/2000/svg"
                      xlinkHref="http://www.w3.org/1999/xlink"
                    >
                      <g
                        id="02.-Result"
                        stroke="none"
                        strokeWidth="1"
                        fill="none"
                        fillRule="evenodd"
                        opacity="0.5"
                      >
                        <g
                          id="result-/-moreBtn"
                          transform="translate(-2.000000, -8.000000)"
                          fill="#FFFFFF"
                        >
                          <path
                            d="M4,12 C2.8954305,12 2,11.1045695 2,10 C2,8.8954305 2.8954305,8 4,8 C5.1045695,8 6,8.8954305 6,10 C6,11.1045695 5.1045695,12 4,12 Z M10,12 C8.8954305,12 8,11.1045695 8,10 C8,8.8954305 8.8954305,8 10,8 C11.1045695,8 12,8.8954305 12,10 C12,11.1045695 11.1045695,12 10,12 Z M16,12 C14.8954305,12 14,11.1045695 14,10 C14,8.8954305 14.8954305,8 16,8 C17.1045695,8 18,8.8954305 18,10 C18,11.1045695 17.1045695,12 16,12 Z"
                            id="Combined-Shape"
                          />
                        </g>
                      </g>
                    </svg>
                  </a>
                </div>
              </h2>
              <HeaderLink />
            </div>
          </div>
        </div>
        <div
          className="contents_summary"
          ref={this.setPanelMainRef}
        >
          <div className="contents_summary_inner">
            <div className="contents_summary_box">
              <div className="overview">
                {companyName !== null ? (
                  <table className="summary_item_table">
                    <colgroup>
                      <col width="8%" />
                      <col width="20%" />
                      <col width="20%" />
                      <col width="20%" />
                      <col width="16%" />
                      <col width="16%" />
                    </colgroup>
                    <tbody>
                      <tr>
                        <td>
                          <div className="summary_item">
                            <p className="label">{t('panel.menu.grade')}</p>
                            <Popup
                              className="grade_info"
                              trigger={<p className="value">{companyGrade && companyGrade}</p>}
                              inverted
                            >
                              <PopupHeader>
                                <Icon name="info circle" />
                                {companyGrade === 'A' && t('panel.menu.info_grade_a_title')}
                                {companyGrade === 'B' && t('panel.menu.info_grade_b_title')}
                                {companyGrade === 'C' && t('panel.menu.info_grade_c_title')}
                                {companyGrade === 'D' && t('panel.menu.info_grade_d_title')}
                              </PopupHeader>
                              <PopupContent>
                                {companyGrade === 'A' && t('panel.menu.info_grade_a')}
                                {companyGrade === 'B' && t('panel.menu.info_grade_b')}
                                {companyGrade === 'C' && t('panel.menu.info_grade_c')}
                                {companyGrade === 'D' && t('panel.menu.info_grade_d')}
                              </PopupContent>
                            </Popup>
                          </div>
                        </td>
                        <td>
                          <div className="summary_item">
                            <p className="label">{t('panel.menu.component')}</p>
                            <p className="value">
                              {componentCount ? this.abbreviateNumber(componentCount.total) : 0 }
                            </p>
                          </div>
                          <div className="summary_chart pie">
                            <span className="value">
                              {componentCount ? this.abbreviateNumber(componentCount.has_risk) : 0 }
                            </span>
                            <canvas
                              ref={(ref) => {
                                this.pieChart = ref;
                              }}
                            />
                          </div>
                        </td>
                        <td>
                          <div className="summary_item">
                            <p className="label">{t('panel.menu.security_risk')}</p>
                            <p className="value">
                              {securityCount ? this.abbreviateNumber(securityCount.total) : 0}
                              {hasExploit ? (<Icon name="bug" />) : null}
                            </p>
                          </div>
                          <Popup
                            trigger={(
                              <div className="summary_chart square_chart">
                                <div>
                                  {securityCount ? this.abbreviateNumber(securityCount.critical) : 0}
                                </div>
                                <div>
                                  {securityCount ? this.abbreviateNumber(securityCount.low) : 0}
                                </div>
                                <div>
                                  {securityCount ? this.abbreviateNumber(securityCount.high) : 0}
                                </div>
                                <div>
                                  {securityCount ? this.abbreviateNumber(securityCount.medium) : 0}
                                </div>
                              </div>
                            )}
                            inverted
                          >
                            <List>
                              <List.Item>
                                <List.Icon
                                  name="circle"
                                  style={{ color: '#ff440c', fontSize: '11px' }}
                                />
                                <span className="tip red">{t('panel.menu.red')}</span>
                                <span className="tip">{`= ${t('panel.menu.critical')}`}</span>
                              </List.Item>
                              <List.Item>
                                <List.Icon
                                  name="circle"
                                  style={{ color: '#ff7d00', fontSize: '11px' }}
                                />
                                <span className="tip dark_orange">{t('panel.menu.dark_orange')}</span>
                                <span className="tip">{`= ${t('panel.menu.high')}`}</span>
                              </List.Item>
                              <List.Item>
                                <List.Icon
                                  name="circle"
                                  style={{ color: '#ffae00', fontSize: '11px' }}
                                />
                                <span className="tip orange">{t('panel.menu.orange')}</span>
                                <span className="tip">{`= ${t('panel.menu.medium')}`}</span>
                              </List.Item>
                              <List.Item>
                                <List.Icon
                                  name="circle"
                                  style={{ color: '#ffde00', fontSize: '11px' }}
                                />
                                <span className="tip yellow">{t('panel.menu.yellow')}</span>
                                <span className="tip">{`= ${t('panel.menu.low')}`}</span>
                              </List.Item>
                            </List>
                          </Popup>
                        </td>
                        <td>
                          <div className="summary_item">
                            <p className="label">{t('panel.menu.license')}</p>
                            <p className="value">
                              {licenseCount ? this.abbreviateNumber(licenseCount.total) : 0}
                            </p>
                          </div>
                          <Popup
                            trigger={(
                              <div className="summary_chart bubble_chart">
                                <div className="">
                                  <div className="inner">
                                    {licenseCount ? this.abbreviateNumber(licenseCount.protective) : 0}
                                  </div>
                                </div>
                                <div>
                                  <div className="inner">
                                    {licenseCount ? this.abbreviateNumber(licenseCount.permissive) : 0}
                                  </div>
                                </div>
                                <div>
                                  <div className="inner">
                                    {licenseCount ? this.abbreviateNumber(licenseCount.others) : 0}
                                  </div>
                                </div>
                              </div>
                            )}
                            inverted
                          >
                            <List>
                              <List.Item>
                                <List.Icon
                                  name="square"
                                  style={{ color: '#ff440c', fontSize: '11px' }}
                                />
                                <span className="tip red">{t('panel.menu.red')}</span>
                                <span className="tip">{`= ${t('panel.menu.protective')}`}</span>
                              </List.Item>
                              <List.Item>
                                <List.Icon
                                  name="square"
                                  style={{ color: '#ffae00', fontSize: '11px' }}
                                />
                                <span className="tip orange">{t('panel.menu.orange')}</span>
                                <span className="tip">{`= ${t('panel.menu.permissive')}`}</span>
                              </List.Item>
                              <List.Item>
                                <List.Icon
                                  name="square"
                                  style={{ color: '#ffde00', fontSize: '11px' }}
                                />
                                <span className="tip yellow">{t('panel.menu.yellow')}</span>
                                <span className="tip">{`= ${t('panel.menu.others')}`}</span>
                              </List.Item>
                            </List>
                          </Popup>
                        </td>
                        <td>
                          <div className="summary_item">
                            <p className="label">{t('panel.menu.litigator')}</p>
                            <p className="value">{litigatorCount}</p>
                          </div>
                        </td>
                        <td>
                          <div className="summary_item">
                            <p className="label">{t('panel.menu.violated_policy')}</p>
                            <p className="value">{policyCount}</p>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                ) : (
                  <table className="summary_item_table">
                    <colgroup>
                      <col width="8%" />
                      <col width="20%" />
                      <col width="20%" />
                      <col width="20%" />
                      <col width="16%" />
                      <col width="16%" />
                    </colgroup>
                    <tbody>
                      <tr>
                        <td>
                          <div className="summary_item">
                            <p className="label">{t('panel.menu.grade')}</p>
                            <p className="value empty">A</p>
                          </div>
                        </td>
                        <td>
                          <div className="summary_item">
                            <p className="label">{t('panel.menu.component')}</p>
                            <p className="value empty">0</p>
                          </div>
                          <div className="summary_chart pie empty">
                            <span className="value">0</span>
                            <canvas
                              ref={(ref) => {
                                this.pieChart = ref;
                              }}
                              //style={{ width: '68px', height: '68px' }}
                            />
                          </div>
                        </td>
                        <td>
                          <div className="summary_item">
                            <p className="label">{t('panel.menu.security_risk')}</p>
                            <p className="value empty">0</p>
                          </div>
                          <div className="summary_chart square_chart">
                            <div className="empty">0</div>
                            <div className="empty">0</div>
                            <div className="empty">0</div>
                            <div className="empty">0</div>
                          </div>
                        </td>
                        <td>
                          <div className="summary_item">
                            <p className="label">{t('panel.menu.license')}</p>
                            <p className="value empty">0</p>
                          </div>
                          <div className="summary_chart bubble_chart">
                            <div className="empty">
                              <div className="inner">0</div>
                            </div>
                            <div className="empty">
                              <div className="inner">0</div>
                            </div>
                            <div className="empty">
                              <div className="inner">0</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="summary_item">
                            <p className="label">{t('panel.menu.litigator')}</p>
                            <p className="value empty">0</p>
                          </div>
                        </td>
                        <td>
                          <div className="summary_item">
                            <p className="label">{t('panel.menu.violated_policy')}</p>
                            <p className="value empty">0</p>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default withTranslation()(inject('state')(observer(OverviewPanel)));
